import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowForward, Refresh, Search, Settings } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import PageHeader from "../components/ui/PageHeader";
import OverviewMetrics from "../components/ui/OverviewMetrics";
import { formatLicenseDate } from '../utils/licenses';
import MachineLicenseChips from "../components/MachineLicenseChips";

const statusLabels = {
  online: "Online",
  offline: "Offline",
  maintenance: "Manutenzione",
  error: "Errore",
};
const MachinesPage: React.FC = () => {
  const { state, selectMachine, refreshMachines } = useApp();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const canManage = ["administrator", "reseller"].includes(
    state.auth.user?.role || "",
  );
  useEffect(() => {
    const interval = setInterval(refreshMachines, 30000);
    return () => clearInterval(interval);
  }, [refreshMachines]);
  const refresh = async () => {
    setRefreshing(true);
    try {
      await refreshMachines();
    } finally {
      setRefreshing(false);
    }
  };
  const machines = state.machines;
  const metric = (n: number) =>
    state.machinesError || (state.machinesLoading && !state.machinesUpdatedAt)
      ? "—"
      : n;
  const visible = machines.filter(
    (m) =>
      (!status ||
        (status === "attention"
          ? ["maintenance", "error"].includes(m.status)
          : m.status === status)) &&
      [
        m.name,
        m.serialNumber,
        m.model,
        m.companyName,
        m.location,
        m.resellerName,
      ]
        .join(" ")
        .toLowerCase()
        .includes(search.trim().toLowerCase()),
  );
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Parco macchine"
        subtitle="Stato di connessione, cliente e licenze NextControl. Apri una macchina per vedere telemetria e dati di produzione."
        actions={
          <>
            <Button
              startIcon={<Refresh />}
              disabled={refreshing}
              onClick={() => void refresh()}
            >
              {refreshing ? "Aggiornamento…" : "Aggiorna"}
            </Button>
            {canManage && (
              <Button
                variant="contained"
                startIcon={<Settings />}
                onClick={() => navigate("/machines/manage")}
              >
                Gestisci macchine
              </Button>
            )}
          </>
        }
      />
      <OverviewMetrics
        items={[
          {
            label: "Macchine totali",
            value: metric(machines.length),
            detail: "Associate al tuo account",
            onClick: () => setStatus(""),
            selected: status === "",
          },
          {
            label: "Online",
            value: metric(machines.filter((m) => m.status === "online").length),
            detail: "Collegate al sistema",
            color: "success.main",
            onClick: () => setStatus("online"),
            selected: status === "online",
          },
          {
            label: "Offline",
            value: metric(
              machines.filter((m) => m.status === "offline").length,
            ),
            detail: "Controlla l’ultimo contatto",
            onClick: () => setStatus("offline"),
            selected: status === "offline",
          },
          {
            label: "Da verificare",
            value: metric(
              machines.filter((m) =>
                ["error", "maintenance"].includes(m.status),
              ).length,
            ),
            detail: "Errori o manutenzione",
            color: "warning.main",
            onClick: () => setStatus("attention"),
            selected: status === "attention",
          },
        ]}
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          size="small"
          label="Cerca macchine"
          placeholder="Nome, matricola, cliente o modello"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Stato macchina"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">Tutti gli stati</MenuItem>
          {Object.entries(statusLabels).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
          <MenuItem value="attention">Da verificare</MenuItem>
        </TextField>
        {!!(search || status) && (
          <Button
            onClick={() => {
              setSearch("");
              setStatus("");
            }}
          >
            Azzera filtri
          </Button>
        )}
      </Stack>
      {state.machinesError && (
        <Alert severity="error">{state.machinesError}</Alert>
      )}
      {state.machinesLoading && !state.machinesUpdatedAt && (
        <Alert severity="info">Caricamento delle macchine…</Alert>
      )}
      <Typography variant="body2" color="text.secondary">
        {visible.length} di {machines.length} macchine · aggiornamento
        automatico ogni 30 secondi
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "repeat(2, minmax(0, 1fr))",
          },
          gap: 2.5,
        }}
      >
        {visible.map((machine) => (
          <Card
            key={machine.id}
            sx={{ display: "flex", flexDirection: "column", boxShadow: "none" }}
          >
            <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                spacing={2}
                alignItems="flex-start"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ overflowWrap: "anywhere" }}>
                    {machine.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {machine.model} · Matricola {machine.serialNumber}
                  </Typography>
                </Box>
                <Chip
                  variant="outlined"
                  size="small"
                  label={statusLabels[machine.status] || machine.status}
                  color={
                    machine.status === "online"
                      ? "success"
                      : machine.status === "error"
                        ? "error"
                        : machine.status === "maintenance"
                          ? "warning"
                          : "default"
                  }
                />
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                  my: 2.5,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Cliente
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {machine.companyName || "Non assegnato"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Sede
                  </Typography>
                  <Typography variant="body2">
                    {machine.location || "Non indicata"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Potenza laser
                  </Typography>
                  <Typography variant="body2">
                    {machine.specifications.power
                      ? `${machine.specifications.power.toLocaleString("it-IT")} W`
                      : "Non indicata"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Versione HMI
                  </Typography>
                  <Typography variant="body2">
                    {machine.softwareVersion || "Non disponibile"}
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Licenze e servizi della macchina
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                NextControl:{" "}
                {machine.licenseExpirationDate
                  ? `scadenza ${formatLicenseDate(machine.licenseExpirationDate)}`
                  : "senza scadenza"}
              </Typography>
              <MachineLicenseChips machine={machine} />
              {machine.currentProduction && (
                <Alert
                  icon={false}
                  severity={
                    machine.status === "online" && machine.mode === "PRODUCTION"
                      ? "success"
                      : "info"
                  }
                  sx={{ mt: 2 }}
                >
                  <strong>{machine.currentProduction.code}</strong> ·{" "}
                  {machine.currentProduction.partsCompleted}/
                  {machine.currentProduction.totalParts} pezzi completati
                  {machine.status !== "online" && " · Ultimo dato ricevuto"}
                </Alert>
              )}
            </CardContent>
            <CardActions
              sx={{
                px: 2.5,
                py: 1.5,
                borderTop: "1px solid",
                borderColor: "divider",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Ultimo contatto:{" "}
                {machine.lastSeen
                  ? new Date(machine.lastSeen).toLocaleString("it-IT")
                  : "Mai ricevuto"}
              </Typography>
              <Button
                endIcon={<ArrowForward />}
                onClick={() => {
                  selectMachine(machine);
                  navigate(`/machines/${machine.id}`);
                }}
                aria-label={`Apri macchina ${machine.name}`}
              >
                Apri dettagli
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>
      {!visible.length && !state.machinesLoading && !state.machinesError && (
        <Card sx={{ p: 5, textAlign: "center", boxShadow: "none" }}>
          <Typography variant="h6">
            {machines.length
              ? "Nessuna macchina corrisponde ai filtri"
              : "Nessuna macchina associata"}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {machines.length
              ? "Prova un’altra matricola o azzera i filtri."
              : "Le macchine associate al tuo account appariranno qui."}
          </Typography>
        </Card>
      )}
    </Stack>
  );
};
export default MachinesPage;
